/**
 * Nadel-Spalten am Mangel — `inspection_items.plan_id`, `plan_coordinates`
 * und `verortung` (SQL Nr. 130, supabase/verortung_pins.sql).
 *
 * Vertrag: docs/planverortung-vertrag.md (Paket P1). Die Nadeln selbst liest
 * und normalisiert ausschliesslich services/planPins.ts — hier wird nur
 * zwischen Eintrag und DB-Zeile uebersetzt.
 *
 * ─── Schreiben: IMMER alle drei Schluessel ─────────────────────────────────
 * Anders als die Katalog-Spalten (Nr. 131, nur definierte Felder): der Client
 * liest die drei Spalten jetzt bei jedem Laden (`loadProjectById`), und am
 * Eintrag heisst `undefined` „keine Nadel“ — eine entfernte Nadel muss also
 * als NULL in der Datenbank ankommen, sonst waere sie nach dem Neuladen
 * zurueck. Deshalb stehen die drei Schluessel immer in der Zeile, NULL = keine
 * Nadel / keine Sensor-Verortung.
 *
 * Alte Clients schicken die Schluessel gar nicht: `save_project_children_tx`
 * baut seine Spaltenliste aus `jsonb_object_keys` und setzt nur mitgelieferte
 * Spalten — der Bestand bleibt dann stehen (Kopf von verortung_pins.sql,
 * „Warum alte Sitzungen die Pins nicht loeschen“). Dieselbe RPC laesst
 * unbekannte Spalten weg: ohne Nr. 130 fallen die Schluessel still weg
 * (fail-open). Der Direkt-Upsert in `restoreInspectionItems` filtert nicht —
 * dort gibt es den Rueckfall ohne diese Spalten (`ohnePinSpalten`).
 *
 * ─── Form wie die CHECKs ──────────────────────────────────────────────────
 * `inspection_items_plan_coordinates_chk` verlangt Array oder Objekt,
 * `inspection_items_verortung_chk` ein Objekt. Ein verletzter CHECK bricht das
 * GANZE Projekt-Speichern ab — deshalb geht nur ein normalisiertes Array
 * (`pinFelder(pinsVon(item))`, jede Nadel mit ihrem Plan) bzw. ein schlichtes
 * Objekt hinaus, alles andere wird NULL.
 */
import type { EintragVerortung, InspectionItem, PlanCoordinates } from '../../types';
import { pinFelder, pinsVon } from '../planPins';

/** Die drei Spaltennamen, z. B. fuer den Rueckfall ohne Nr. 130. */
export const PIN_SPALTEN = ['plan_id', 'plan_coordinates', 'verortung'] as const;

export type PinSpalte = (typeof PIN_SPALTEN)[number];

/** Die Nadel-Felder am Eintrag. */
export type PinQuellFelder = Pick<InspectionItem, 'planId' | 'planCoordinates' | 'verortung'>;

/**
 * Der Nadel-Teil einer DB-Zeile — immer alle drei Schluessel. Typ-Alias statt
 * interface, damit die Zeile als `Record<string, unknown>` durchgeht.
 */
export type PinSpaltenZeile = {
    plan_id: string | null;
    plan_coordinates: PlanCoordinates[] | null;
    verortung: EintragVerortung | null;
};

/** Was `pinFelderLesen` liefert: nur gueltige Werte, Nadeln immer als Array. */
export interface GelesenePinFelder {
    planId?: string;
    planCoordinates?: PlanCoordinates[];
    verortung?: EintragVerortung;
}

const PIN_SPALTEN_MENGE: ReadonlySet<string> = new Set(PIN_SPALTEN);

/** Schlichtes Objekt: kein Array, kein null, keine Klasseninstanz (Date o. ae.). */
const istSchlichtesObjekt = (wert: unknown): wert is Record<string, unknown> => {
    if (!wert || typeof wert !== 'object' || Array.isArray(wert)) return false;
    const proto = Object.getPrototypeOf(wert);
    return proto === Object.prototype || proto === null;
};

/** Das, was `pinFelderLesen` auch wieder liest — sonst ginge die Rundreise schief. */
const istSensorVerortung = (wert: unknown): wert is EintragVerortung =>
    istSchlichtesObjekt(wert) && wert.quelle === 'sensor';

/**
 * Baut den Nadel-Teil einer `inspection_items`-Zeile.
 *
 * IMMER alle drei Schluessel. Nadeln normalisiert ueber
 * `pinFelder(pinsVon(item))` (ungueltige Zahlen und Nadeln ohne Plan fallen
 * weg, jede Nadel traegt ihren Plan, `plan_id` = Plan der ersten Nadel).
 * `verortung` als schlichtes Sensor-Objekt, AUCH ohne Nadel: es traegt dann
 * nur das Geschoss (Geschoss-Knopf, Auto-Ort ohne Planpunkt) und ginge sonst
 * beim Speichern verloren (Codex-Review 28.09., P1). Welche Nadel zum Blick
 * gehoert, sagt die Markierung `sensor` an der Nadel (planPins).
 */
export const pinSpaltenSchreiben = (item: PinQuellFelder): PinSpaltenZeile => {
    const felder = pinFelder(pinsVon(item));
    return {
        plan_id: felder.planId ?? null,
        plan_coordinates: felder.planCoordinates ?? null,
        verortung: istSensorVerortung(item.verortung) ? item.verortung : null,
    };
};

/**
 * Liest die Nadel-Felder aus einer DB-Zeile (`select('*')`). Nur gueltige
 * Werte: NULL oder eine fehlende Spalte (Nr. 130 noch nicht gelaufen) laesst
 * den Schluessel weg.
 *
 *   plan_id           nicht-leerer String
 *   plan_coordinates  Array oder Objekt (Altstand), normalisiert ueber
 *                     planPins — nur mit mindestens einer gueltigen Nadel;
 *                     dann ist `planId` der Plan der ersten Nadel
 *   verortung         Objekt mit `quelle === 'sensor'`
 */
export const pinFelderLesen = (dbRow: Record<string, unknown>): GelesenePinFelder => {
    const felder: GelesenePinFelder = {};

    const planId = typeof dbRow.plan_id === 'string' && dbRow.plan_id.trim() !== '' ? dbRow.plan_id : undefined;
    if (planId !== undefined) felder.planId = planId;

    const verortung = dbRow.verortung;
    if (istSensorVerortung(verortung)) felder.verortung = verortung;

    const roh = dbRow.plan_coordinates;
    if (roh && typeof roh === 'object') {
        const normalisiert = pinFelder(pinsVon({ planId, planCoordinates: roh as PlanCoordinates | PlanCoordinates[], verortung: felder.verortung }));
        if (normalisiert.planCoordinates !== undefined && normalisiert.planId !== undefined) {
            felder.planId = normalisiert.planId;
            felder.planCoordinates = normalisiert.planCoordinates;
        }
    }

    return felder;
};

/** Traegt die Zeile mindestens einen Nadel-Schluessel (auch mit null)? */
export const hatPinSpalten = (zeile: object): boolean =>
    Object.keys(zeile).some((schluessel) => PIN_SPALTEN_MENGE.has(schluessel));

/** Kopie der Zeile ohne die Nadel-Schluessel (Rueckfall ohne Nr. 130). */
export const ohnePinSpalten = <T extends object>(zeile: T): Omit<T, PinSpalte> =>
    Object.fromEntries(
        Object.entries(zeile).filter(([schluessel]) => !PIN_SPALTEN_MENGE.has(schluessel)),
    ) as Omit<T, PinSpalte>;
